import { v4 as uuidv4 } from "uuid";
import { FormConfiguration } from "../../shared/crud/form";
import { Checkbox } from "../../shared/widgets/checkbox";
import SectionUnderline from "../../shared/widgets/sectionUnderline";
import MaxAccommodationsMultiReservation from "../maxAccommodationsMultiReservation";
import MultiStayDisabledInfo from "../MultiStayDisabledInfo";
import { CustomEstablishmentOrderWidget } from "../CustomEstablishmentOrderWidget";
import { HighlightReelOrderWidget } from "../HighlightReelOrderWidget";
import { messages } from "../messages";
import {
  AccommodationSortingCriteria,
  BehaviourEntity,
  BundlingConfiguration,
  DepositCheckoutTextConfiguration,
  DepositNotRequiredTextConfiguration,
  DepositRequiredTextConfiguration,
  ExtrasOrderCriteria,
  ExtrasTextConfiguration,
  NavigationStyle,
  PendingAmountTextConfiguration,
  PriceCalendar,
  PromosConfig,
  RateType,
  RoomSelectionViewType,
  ShowExtrasConfiguration,
  ShowPromoCodeCriteria,
  SplitStay,
  TaxesText,
} from "./behaviour.entity";
import schema from "./behaviour.schema.json";
import { GroupBookingModule } from "../GroupBookingModule";
import { PropertyConfigurationValue } from "../../../components/property-configuration-provider";

export function buildFormConfiguration(
  config: PropertyConfigurationValue,
  isAdmin: boolean = false,
  isChain: boolean = false
): FormConfiguration<BehaviourEntity> {
  const disabledWhenSingleStay = (_isNew: boolean, formData?: BehaviourEntity) =>
    formData?.allowMultiStayCart === false;

  return {
    __fieldOrder: [
      "showElementsSection",
      "showBookingForm",
      "showCancelMsg",
      "showLanguageSelector",
      "showCurrencySelector",
      "showAllReservationsIds",
      "showFullReservationIds",
      "hideObligatoryExtras",
      "showBestPriceGuaranteed",
      "showCookiePolicy",
      "showInventoryPriceDiff",
      "waitingListEnabled",
      "showCheckoutButtonRoomUnits",
      "showInactivityModal",
      "allowMultiStayCart",
      "multiStayDisabledInfo",

      "resultSection",
      "hideLocked",
      "hideRestricted",
      "roomSelectionViewType",
      "splitStayConfiguration",
      "accommodationsOrder",
      "customEstablishmentOrder",
      "maxRoomsInUnitSelector",
      "roomsLeftAvailabilityLimit",
      "bundlingConfiguration",
      "groupBookingSection",
      "groupBookingModuleSection",

      "promosSections",
      "showPromoCodeCriteria",
      "showPromos",
      "showInactiveDiscounts",
      "showRestrictedHighlightMessages",
      "showDiscountsCalculation",

      "textsSection",
      "pendingAmountTextConfiguration",
      "depositRequiredTextConfiguration",
      "depositNotRequiredTextConfiguration",
      "taxesText",
      "depositCheckoutTextConfiguration",
      "extrasTextConfiguration",

      "logoSection",
      "showDesktopLogo",
      "isLogoLinkingToPropertyUrl",
      "propertyUrl",
      "enginePageTitle",

      "otherParamsSection",
      "maxAccommodationsMultiReservationSection",
      "allowMultiplyPriceByUnits",
      "showRackRate",
      "hasRoomCombination",
      "addObligatoryExtrasRateToRoomSelection",
      "showDecimalsInExtrasPage",
      "isCancellationReasonRequired",
      "priceCalendar",
      "showExtrasConfiguration",
      "unavailableURL",
      "taxesRateType",
      "keepOriginalDailyRateOnGuestModification",
      "overrideCurrencyAccordingToLocation",
      "displayAmountsWithoutDecimalsOnSteps",
      "navigationStyle",
      ...(!isChain ? (["useChildEngineCancelPolicyOrderInsteadAgr"] as const) : []),
      "engineHighlightReelOrder",
      "extrasOrderCriteria",
      "*",
    ],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    maxAccommodationsMultiReservation: {
      hidden: true,
    },
    showMultipleAccommodations: {
      hidden: true,
    },
    groupBookingModule: {
      hidden: true,
    },
    groupBookingMinGuests: {
      hidden: true,
    },
    groupBookingMaxGuests: {
      hidden: true,
    },
    groupBookingMaxRooms: {
      hidden: true,
    },
    groupBookingPolicies: {
      hidden: true,
    },
    showElementsSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.showElementsSection,
      },
    },
    logoSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.logoSection,
      },
    },
    textsSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.textsSection,
      },
    },
    resultSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.resultSection,
      },
    },
    promosSections: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.promosSections,
      },
    },
    otherParamsSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.otherParamsSection,
      },
    },
    roomSelectionViewType: {
      messages: {
        label: messages.roomSelectionViewType,
      },
      default: () => RoomSelectionViewType[schema.properties.roomSelectionViewType.enum[0]],
      enumLabels: schema.properties.roomSelectionViewType.enum.map(it => messages[it]),
    },
    splitStayConfiguration: {
      messages: {
        label: messages.splitStayConfiguration,
      },
      readonly: disabledWhenSingleStay,
      default: () => SplitStay[schema.properties.splitStayConfiguration.enum[0]],
      enumLabels: schema.properties.splitStayConfiguration.enum.map(it => messages[it]),
    },
    accommodationsOrder: {
      messages: {
        label: messages.accommodationsOrder,
      },
      default: () => AccommodationSortingCriteria[schema.properties.accommodationsOrder.enum[0]],
      enumLabels: schema.properties.accommodationsOrder.enum.map(it => messages[it]),
    },
    customEstablishmentOrder: {
      fieldWidget: CustomEstablishmentOrderWidget,
      messages: {
        label: messages.customEstablishmentOrder,
      },
    },
    showPromoCodeCriteria: {
      messages: {
        label: messages.showPromoCodeCriteria,
      },
      default: () => ShowPromoCodeCriteria[schema.properties.showPromoCodeCriteria.enum[0]],
      enumLabels: schema.properties.showPromoCodeCriteria.enum.map(it => messages[it]),
    },
    priceCalendar: {
      messages: {
        label: messages.priceCalendar,
      },
      default: () => PriceCalendar[schema.properties.priceCalendar.enum[0]],
      enumLabels: schema.properties.priceCalendar.enum.map(it => messages[it]),
    },
    showExtrasConfiguration: {
      messages: {
        label: messages.showExtrasConfiguration,
      },
      default: () => ShowExtrasConfiguration[schema.properties.showExtrasConfiguration.enum[0]],
      enumLabels: schema.properties.showExtrasConfiguration.enum.map(it => messages[it]),
    },
    navigationStyle: {
      messages: {
        label: messages.navigationStyle,
      },
      default: () => NavigationStyle.SIMPLE,
      enumLabels: schema.properties.navigationStyle.enum.map(it => messages[it]),
    },
    pendingAmountTextConfiguration: {
      messages: {
        label: messages.pendingAmountTextConfiguration,
      },
      default: () =>
        PendingAmountTextConfiguration[schema.properties.pendingAmountTextConfiguration.enum[0]],
      enumLabels: schema.properties.pendingAmountTextConfiguration.enum.map(it => messages[it]),
    },
    depositRequiredTextConfiguration: {
      messages: {
        label: messages.depositRequiredTextConfiguration,
      },
      default: () =>
        DepositRequiredTextConfiguration[
          schema.properties.depositRequiredTextConfiguration.enum[0]
        ],
      enumLabels: schema.properties.depositRequiredTextConfiguration.enum.map(it => messages[it]),
    },
    depositNotRequiredTextConfiguration: {
      messages: {
        label: messages.depositNotRequiredTextConfiguration,
      },
      default: () =>
        DepositNotRequiredTextConfiguration[
          schema.properties.depositNotRequiredTextConfiguration.enum[0]
        ],
      enumLabels: schema.properties.depositNotRequiredTextConfiguration.enum.map(
        it => messages[it]
      ),
    },
    taxesText: {
      messages: {
        label: messages.taxesText,
      },
      default: () => TaxesText.TAXES_INCLUDED,
      enumLabels: schema.properties.taxesText.enum.map(it => messages[it]),
      hidden: true, // Hidden for now, as it is not used in the UI
    },
    depositCheckoutTextConfiguration: {
      messages: {
        label: messages.depositCheckoutTextConfiguration,
      },
      default: () => DepositCheckoutTextConfiguration.DEPOSIT,
      enumLabels: schema.properties.depositCheckoutTextConfiguration.enum.map(it => messages[it]),
    },
    extrasTextConfiguration: {
      messages: {
        label: messages.extrasTextConfiguration,
      },
      default: () => ExtrasTextConfiguration.EXTRAS,
      enumLabels: schema.properties.extrasTextConfiguration.enum.map(it => messages[it]),
    },
    showBookingForm: {
      messages: {
        label: messages.showBookingForm,
      },
      widget: Checkbox,
      default: () => true,
    },
    showDesktopLogo: {
      messages: {
        label: messages.showDesktopLogo,
      },
      widget: Checkbox,
      default: () => true,
    },
    showPromos: {
      messages: {
        label: messages.showPromos,
      },
      widget: Checkbox,
      default: () => true,
    },
    showCancelMsg: {
      messages: {
        label: messages.showCancelMsg,
      },
      widget: Checkbox,
      default: () => true,
    },
    showLanguageSelector: {
      messages: {
        label: messages.showLanguageSelector,
      },
      widget: Checkbox,
      default: () => true,
    },
    showCurrencySelector: {
      messages: {
        label: messages.showCurrencySelector,
      },
      widget: Checkbox,
      default: () => true,
    },
    showDiscountsCalculation: {
      messages: {
        label: messages.showDiscountsCalculation,
      },
      widget: Checkbox,
      default: () => true,
    },
    showAllReservationsIds: {
      messages: {
        label: messages.showAllReservationsIds,
      },
      widget: Checkbox,
      default: () => true,
    },
    showFullReservationIds: {
      messages: {
        label: messages.showFullReservationIds,
      },
      widget: Checkbox,
      default: () => true,
    },
    hideObligatoryExtras: {
      messages: {
        label: messages.hideObligatoryExtras,
      },
      widget: Checkbox,
      default: () => true,
    },
    showBestPriceGuaranteed: {
      messages: {
        label: messages.showBestPriceGuaranteed,
      },
      widget: Checkbox,
      default: () => true,
    },
    maxAccommodationsMultiReservationSection: {
      fieldWidget: MaxAccommodationsMultiReservation,
    },
    showCookiePolicy: {
      messages: {
        label: messages.showCookiePolicy,
      },
      widget: Checkbox,
      default: () => true,
    },
    showInventoryPriceDiff: {
      messages: {
        label: messages.showInventoryPriceDiff,
      },
      widget: Checkbox,
      default: () => true,
    },
    showInactiveDiscounts: {
      messages: {
        label: messages.showInactiveDiscounts,
      },
      default: () => PromosConfig.SHOW_WHEN_APPLICABLE,
      enumLabels: schema.properties.showInactiveDiscounts.enum.map(it => messages[it]),
    },
    showRestrictedHighlightMessages: {
      messages: {
        label: messages.showRestrictedHighlightMessages,
      },
      default: () => PromosConfig.SHOW_WHEN_APPLICABLE,
      enumLabels: schema.properties.showRestrictedHighlightMessages.enum.map(it => messages[it]),
    },
    isLogoLinkingToPropertyUrl: {
      messages: {
        label: messages.isLogoLinkingToPropertyUrl,
      },
      widget: Checkbox,
      default: () => true,
    },
    maxRoomsInUnitSelector: {
      messages: {
        label: messages.maxRoomsInUnitSelector,
      },
      readonly: disabledWhenSingleStay,
    },
    roomsLeftAvailabilityLimit: {
      messages: {
        label: messages.roomsLeftAvailabilityLimit,
      },
    },
    unavailableURL: {
      messages: {
        label: messages.unavailableURL,
      },
    },
    taxesRateType: {
      messages: {
        label: messages.taxesRateType,
      },
      hidden: !isAdmin,
      default: () => RateType[schema.properties.taxesRateType.enum[0]],
      enumLabels: schema.properties.taxesRateType.enum.map(it => messages[it]),
    },
    keepOriginalDailyRateOnGuestModification: {
      messages: {
        label: messages.keepOriginalDailyRateOnGuestModification,
      },
      widget: Checkbox,
      default: () => false,
    },
    enginePageTitle: {
      messages: {
        label: messages.enginePageTitle,
      },
    },
    propertyUrl: {
      messages: {
        label: messages.propertyUrl,
      },
    },
    hideLocked: {
      messages: {
        label: messages.hideLocked,
      },
      widget: Checkbox,
      default: () => true,
    },
    hideRestricted: {
      messages: {
        label: messages.hideRestricted,
      },
      widget: Checkbox,
      default: () => true,
    },
    showRackRate: {
      messages: {
        label: messages.showRackRate,
      },
      widget: Checkbox,
      default: () => true,
    },
    allowMultiplyPriceByUnits: {
      messages: {
        label: messages.allowMultiplyPriceByUnits,
      },
      widget: Checkbox,
      default: () => false,
    },
    hasRoomCombination: {
      messages: {
        label: messages.hasRoomCombination,
      },
      widget: Checkbox,
      default: () => true,
    },
    addObligatoryExtrasRateToRoomSelection: {
      messages: {
        label: messages.addObligatoryExtrasRateToRoomSelection,
      },
      widget: Checkbox,
      default: () => true,
    },
    showDecimalsInExtrasPage: {
      messages: {
        label: messages.showDecimalsInExtrasPage,
      },
      widget: Checkbox,
      default: () => false,
    },
    isCancellationReasonRequired: {
      messages: {
        label: messages.isCancellationReasonRequired,
      },
      widget: Checkbox,
      default: () => false,
    },
    bundlingConfiguration: {
      messages: {
        label: messages.bundlingConfiguration,
      },
      readonly: disabledWhenSingleStay,
      default: () => BundlingConfiguration[schema.properties.bundlingConfiguration.enum[0]],
      enumLabels: schema.properties.bundlingConfiguration.enum.map(it => messages[it]),
    },
    groupBookingSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.groupBookingSection,
      },
      hidden: () => !config?.showGroupBookingModule || config?.showGroupBookingModule !== "1",
    },
    groupBookingModuleSection: {
      fieldWidget: GroupBookingModule,
    },
    waitingListEnabled: {
      messages: {
        label: messages.waitingListEnabled,
      },
      widget: Checkbox,
      default: () => false,
    },
    showCheckoutButtonRoomUnits: {
      messages: {
        label: messages.showCheckoutButtonRoomUnits,
      },
      widget: Checkbox,
      default: () => false,
    },
    overrideCurrencyAccordingToLocation: {
      messages: {
        label: messages.overrideCurrencyAccordingToLocation,
      },
      widget: Checkbox,
      default: () => false,
    },
    displayAmountsWithoutDecimalsOnSteps: {
      messages: {
        label: messages.displayAmountsWithoutDecimalsOnSteps,
      },
      widget: Checkbox,
      default: () => false,
    },
    showInactivityModal: {
      messages: {
        label: messages.showInactivityModal,
      },
      widget: Checkbox,
      default: () => false,
    },
    allowMultiStayCart: {
      messages: {
        label: messages.allowMultiStayCart,
        helpText: messages.allowMultiStayCartHelpText,
      },
      widget: Checkbox,
      default: () => true,
    },
    multiStayDisabledInfo: {
      fieldWidget: MultiStayDisabledInfo,
      hideTitle: true,
    },
    ...(!isChain && {
      useChildEngineCancelPolicyOrderInsteadAgr: {
        messages: {
          label: messages.useChildEngineCancelPolicyOrderInsteadAgr,
          helpText: messages.useChildEngineCancelPolicyOrderInsteadAgrHelpText,
        },
        widget: Checkbox,
        default: () => false,
      },
    }),
    engineHighlightReelOrder: {
      fieldWidget: HighlightReelOrderWidget,
      messages: {
        label: messages.engineHighlightReelOrder,
      },
      default: () => "",
    },
    extrasOrderCriteria: {
      messages: {
        label: messages.extrasOrderCriteria,
      },
      default: () => ExtrasOrderCriteria.DEFAULT,
      enumLabels: schema.properties.extrasOrderCriteria.enum.map(it => messages[it]),
    },
  };
}
